<svelte:options
  customElement={{
    tag: "oc-switch-v1",
    /*                                            */
    extend: window.__components.extend({ formAssociated: true, slots: ["default"] }),
    props: {
      name: { type: "String", reflect: true },
      size: { type: "String", reflect: true },
      labelPlacement: { type: "String", reflect: true, attribute: "label-placement" },
      disabled: { type: "Boolean", reflect: true },
      checked: { type: "Boolean" },
      ocAriaLabel: { type: "String", attribute: "oc-aria-label" },
    },
  }}
/>

<script lang="ts">
  import { onMount } from "svelte";
  import { usePropertyChange } from "../../../common/utils/usePropertyChange";
  import type { Props } from "./SwitchV1.types.js";

  usePropertyChange();

  export let checked: Exclude<Props["checked"], undefined> = false;
  export let value: Exclude<Props["value"], undefined> = "on";
  export let name: Props["name"] = undefined;
  export let size: Props["size"] = "100";

  export let labelPlacement: Props["labelPlacement"] = "right";

  export let disabled: Props["disabled"] = false;
  export let ocAriaLabel: Props["ocAriaLabel"] = undefined;

  export let host: HTMLInputElement;

  export let internals: ElementInternals;

  $: internals?.setFormValue(checked ? value : null);

  host.tabIndex = 0;

  $: host.ariaChecked = `${checked}`;

  host.role = "switch";
  onMount(() => {
    host.addEventListener("keydown", ({ code }: KeyboardEvent) => {
      if (code === "Space" && !disabled) {
        checked = !checked;
      }
    });

    host.addEventListener("click", () => {
      if (disabled) return;
      checked = !checked;
    });
  });
</script>

<div class="switch-container">
  {#if labelPlacement === "left"}
    <label>
      <slot />
    </label>
  {/if}
  <div
    aria-hidden="true"
    class="switch switch--size-{size}"
    class:switch--checked={checked}
    class:switch--disabled={disabled}
  >
    <span
      class="slider slider--size-{size}"
      class:slider--checked--disabled={disabled && checked}
      class:slider--disabled={disabled}
    ></span>
  </div>
  {#if labelPlacement === "right"}
    <label>
      <slot />
    </label>
  {/if}
</div>

<style lang="scss">
  @use "@otto-ec/design-tokens/tokens";
  @use "../../../common/scss/mixins/mixins.scss";
  :host(:focus-visible:not([disabled])) {
    outline: none;

    & > .switch-container > .switch {
      &:before {
        @include mixins.focus-common-styles(16px);
      }
    }
  }

  :host(:not([disabled]):hover) {
    .switch-container,
    label {
      cursor: pointer;
    }
  }

  .switch-container {
    outline: none;
    display: flex;
    align-items: flex-start;
    gap: 8px;

    label {
      padding-top: 8px;
    }
  }

  .switch {
    position: relative;
    display: inline-block;
    padding-left: 48px;
    margin-bottom: 12px;
  }

  .switch {
    &--size-50 {
      margin-bottom: tokens.$oc-semantic-spacing-150;
      padding-left: 36px;
      @include mixins.focus-styles(24px);
    }

    &--size-100 {
      margin-bottom: 32px;
      padding-left: 48px;
    }

    &:before {
      position: absolute;
      content: "";
      top: 0;
      left: 0;
      background-color: tokens.$oc-component-switch-default-background-color;
      transition:
        background-color 0.4s,
        opacity 0.4s;
      border-radius: 17px;



      /**
 *
 *
 *
 */

      /*                                             */
      :not(:hover, :active, :focus) & {
        transition: none;
      }

      :focus & {
        @include mixins.focus-common-styles(17px);
      }
      @supports selector(*:focus-visible) {
        :focus & {
          outline: none;
        }

        :focus-visible & {
          @include mixins.focus-common-styles(17px);
        }
      }


      @media (hover: hover) {
        :hover & {
          background: tokens.$oc-component-switch-default-background-color-hover;
          transition: none;
        }
      }
      :active & {
        background-color: tokens.$oc-component-switch-default-background-color-active;
        transition: none;

      }
      :focus & {
        transition: none;
        background: tokens.$oc-component-switch-default-background-color-hover;
      }
    }

    &--size-100:before {
      height: 32px;
      width: 48px;
    }

    &--size-50:before {
      height: 24px;
      width: 36px;
    }

    &--disabled:before {
      cursor: default;
      background-color: tokens.$oc-semantic-color-disabled-background;

      :hover,
      :active,
      :focus & {
        background-color: tokens.$oc-semantic-color-disabled-background;
      }
    }

    &--disabled {
      cursor: default;
    }

    &--checked:before {
      background-color: tokens.$oc-component-switch-checked-background-color;

      @media (hover: hover) {
        :hover & {
          background-color: tokens.$oc-component-switch-checked-background-color-hover;
          transition: none;
        }
      }
      :active & {
        background-color: tokens.$oc-base-color-corporate-color-spectrum-positive-green-200;
        transition: none;
      }
    }

    &--checked {
      &.switch--disabled:before {
        background-color: tokens.$oc-semantic-color-disabled-background;
      }
    }
  }

  .slider {
    position: absolute;
    content: "";
    left: 4px;
    top: 4px;
    background-color:  tokens.$oc-base-color-white;
    transition: transform 0.4s ease-in-out;
    border-radius: 50%;

    &--disabled {
      background-color: tokens.$oc-component-switch-disabled-handle-color;

    }

    &--size-100 {
      width: 24px;
      height: 24px;
    }

    &--size-50 {
      width: 16px;
      height: 16px;
    }

    &:before {
      content: "";
      position: absolute;
      left: 4px;
      top: 4px;
      bottom: 4px;
      right: 4px;
      border-radius: 50%;
      opacity: 0;
      transition: opacity 0.4s ease-in-out;
      mask-image: url("/assets-static/icons/pl_icon_check50.svg");
      background-color: tokens.$oc-component-switch-checked-icon-color;
    }

    &--checked {
      &:before {
        background-color: tokens.$oc-component-switch-checked-icon-color;
      }
      &--disabled {
        background-color: tokens.$oc-component-switch-disabled-handle-color;
        cursor: default;

        &:before {
          background-color: tokens.$oc-component-switch-disabled-background-color;
        }
      }
    }
  }

  :host([aria-checked="true"]) .slider:before {
    opacity: 1;
  }

  :host([aria-checked="true"]) .slider--size-100 {
    transform: translateX(16px);
  }

  :host([aria-checked="true"]) .slider--size-50 {
    transform: translateX(12px);
  }
</style>
